import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Download,
  Edit3,
  Eye,
  FileText,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-react";
import {
  CertificationButton,
  CertificationDataTable,
  CertificationFilters,
  CertificationIconButton,
  CertificationInputField,
  CertificationOverviewCard,
  CertificationSelectField,
  CertificationStandardHero,
  type CertificationTableColumn,
} from "../../shared/ui";
import {
  bibliotecaApiError,
  deleteBibliotecaDocument,
  downloadBibliotecaDocument,
  getBibliotecaDocuments,
  updateBibliotecaDocument,
  uploadBibliotecaDocument,
} from "../api/biblioteca";
import { BibliotecaDateRangeFilter } from "../components/BibliotecaDateRangeFilter";
import { BibliotecaDeleteDialog } from "../components/BibliotecaDeleteDialog";
import { BibliotecaDocumentModal } from "../components/BibliotecaDocumentModal";
import { BibliotecaPreviewModal } from "../components/BibliotecaPreviewModal";
import type {
  BibliotecaDocumento,
  BibliotecaFilters,
  BibliotecaListResponse,
  BibliotecaMetadataPayload,
  BibliotecaStandard,
  BibliotecaTipo,
} from "../types/biblioteca";
import { bibliotecaStandardLabels, bibliotecaStandardRoutes, bibliotecaTipoLabels } from "../types/biblioteca";
import "../../standards/iso-27001-27701/styles/iso-27001-27701.css";
import "../../standards/iso-9001-14001/styles/iso-9001-14001.css";
import "../styles/biblioteca.css";

type Props = {
  standard: BibliotecaStandard;
  tipo: BibliotecaTipo;
};

const initialFilters: BibliotecaFilters = {
  search: "",
  estado: "",
  carga_desde: "",
  carga_hasta: "",
  vigencia_desde: "",
  vigencia_hasta: "",
  page: 1,
};

export function BibliotecaDocumentosPage({ standard, tipo }: Props) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(initialFilters);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [response, setResponse] = useState<BibliotecaListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formDocument, setFormDocument] = useState<BibliotecaDocumento | null | undefined>(undefined);
  const [previewDocument, setPreviewDocument] = useState<BibliotecaDocumento | null>(null);
  const [deleteDocument, setDeleteDocument] = useState<BibliotecaDocumento | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [downloadId, setDownloadId] = useState<number | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(filters.search.trim());
      setFilters((current) => ({ ...current, page: 1 }));
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [filters.search]);

  const requestFilters = useMemo(
    () => ({
      search: debouncedSearch,
      estado: filters.estado,
      carga_desde: filters.carga_desde,
      carga_hasta: filters.carga_hasta,
      vigencia_desde: filters.vigencia_desde,
      vigencia_hasta: filters.vigencia_hasta,
      page: filters.page,
    }),
    [
      debouncedSearch,
      filters.carga_desde,
      filters.carga_hasta,
      filters.estado,
      filters.page,
      filters.vigencia_desde,
      filters.vigencia_hasta,
    ],
  );

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);

      try {
        const data = await getBibliotecaDocuments(standard, tipo, requestFilters, signal);
        setResponse(data);
      } catch (requestError: unknown) {
        if (signal?.aborted) return;
        setError(
          bibliotecaApiError(
            requestError,
            `No se pudieron cargar los documentos de ${bibliotecaTipoLabels[tipo]}.`,
          ),
        );
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [requestFilters, standard, tipo],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const rows = response?.data ?? [];
  const summary = response?.summary;
  const meta = response?.meta;
  const canManage = response?.can_manage ?? false;

  const setFilter = <K extends keyof BibliotecaFilters>(key: K, value: BibliotecaFilters[K]) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      page: key === "page" ? (value as number) : 1,
    }));
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setDebouncedSearch("");
  };

  const submitUpload = async (file: File, payload: BibliotecaMetadataPayload) => {
    setSubmitting(true);
    setFormError(null);
    try {
      await uploadBibliotecaDocument(standard, tipo, file, payload);
      setFormDocument(undefined);
      await load();
    } catch (requestError: unknown) {
      setFormError(bibliotecaApiError(requestError, "No se pudo subir el documento PDF."));
    } finally {
      setSubmitting(false);
    }
  };

  const submitUpdate = async (payload: BibliotecaMetadataPayload) => {
    if (!formDocument) return;
    setSubmitting(true);
    setFormError(null);
    try {
      await updateBibliotecaDocument(standard, tipo, formDocument.id, payload);
      setFormDocument(undefined);
      await load();
    } catch (requestError: unknown) {
      setFormError(bibliotecaApiError(requestError, "No se pudieron actualizar los datos."));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteDocument) return;
    setSubmitting(true);
    setDeleteError(null);
    try {
      await deleteBibliotecaDocument(standard, tipo, deleteDocument.id);
      setDeleteDocument(null);
      await load();
    } catch (requestError: unknown) {
      setDeleteError(bibliotecaApiError(requestError, "No se pudo eliminar el documento."));
    } finally {
      setSubmitting(false);
    }
  };

  const download = async (document: BibliotecaDocumento) => {
    setDownloadId(document.id);
    try {
      await downloadBibliotecaDocument(standard, tipo, document.id, document.nombre_documento);
    } catch (requestError: unknown) {
      setError(bibliotecaApiError(requestError, "No se pudo descargar el documento."));
    } finally {
      setDownloadId(null);
    }
  };

  const columns = useMemo<CertificationTableColumn<BibliotecaDocumento>[]>(
    () => [
      {
        key: "acciones",
        header: "Acciones",
        scrollable: false,
        requiresManage: false,
        render: (document) => (
          <div className="biblioteca-row-actions">
            <CertificationIconButton
              requiresManage={false}
              label="Visualizar"
              icon={<Eye size={15} />}
              onClick={() => setPreviewDocument(document)}
            />
            <CertificationIconButton
              requiresManage={false}
              label="Descargar"
              icon={downloadId === document.id
                ? <Loader2 className="animate-spin" size={15} />
                : <Download size={15} />}
              disabled={downloadId === document.id}
              onClick={() => void download(document)}
            />
            {canManage ? (
              <>
                <CertificationIconButton
                  label="Editar"
                  icon={<Edit3 size={15} />}
                  onClick={() => {
                    setFormError(null);
                    setFormDocument(document);
                  }}
                />
                <CertificationIconButton
                  label="Eliminar"
                  danger
                  icon={<Trash2 size={15} />}
                  onClick={() => {
                    setDeleteError(null);
                    setDeleteDocument(document);
                  }}
                />
              </>
            ) : null}
          </div>
        ),
      },
      {
        key: "created_at",
        header: "Fecha de carga",
        scrollable: false,
        render: (document) => formatDateTime(document.created_at),
      },
      {
        key: "nombre_documento",
        header: "Nombre del documento",
        render: (document) => (
          <div className="biblioteca-document-cell">
            <button
              type="button"
              className="biblioteca-document-name"
              title={document.nombre_documento}
              onClick={() => setPreviewDocument(document)}
            >
              {document.nombre_documento}
            </button>
            <small>{formatBytes(document.tamano_bytes)}</small>
          </div>
        ),
      },
      {
        key: "fecha_vigencia",
        header: "Fecha de vigencia",
        scrollable: false,
        render: (document) => formatDate(document.fecha_vigencia),
      },
      {
        key: "estado",
        header: "Estado",
        scrollable: false,
        render: (document) => <StatusBadge status={document.estado_vigencia} />,
      },
      {
        key: "observaciones",
        header: "Observaciones",
        render: (document) => document.observaciones || "—",
      },
    ],
    [canManage, downloadId],
  );

  return (
    <div className="page-shell biblioteca-page-shell">
      <section className="biblioteca-section-stack">
        <CertificationStandardHero
          title={`Biblioteca · ${bibliotecaStandardLabels[standard]} · ${bibliotecaTipoLabels[tipo]}`}
          description={`Archivo de documentación externa clasificada en ${bibliotecaTipoLabels[tipo].toLowerCase()}.`}
          secondaryDescription="Consulta la vigencia, previsualiza y descarga documentos con acceso autenticado."
          onBack={() => void navigate({ to: bibliotecaStandardRoutes[standard] })}
          backLabel="Volver a Biblioteca"
          actions={
            <CertificationButton
              variant="secondary"
              icon={loading ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
              onClick={() => void load()}
              disabled={loading}
            >
              Actualizar
            </CertificationButton>
          }
          stats={[
            { icon: FileText, label: "Documentos", value: summary?.total ?? 0, loading },
            { icon: CheckCircle2, label: "Vigentes", value: summary?.vigentes ?? 0, loading },
            { icon: AlertTriangle, label: "Caducados", value: summary?.caducados ?? 0, loading },
          ]}
        />

        <div className="iso27001-overview-grid biblioteca-overview-grid-shared">
          <CertificationOverviewCard icon={FileText} label="Total documentos" value={summary?.total ?? 0} />
          <CertificationOverviewCard icon={CheckCircle2} label="Vigentes" value={summary?.vigentes ?? 0} />
          <CertificationOverviewCard icon={Clock3} label="Próximos a vencer" value={summary?.proximos_vencer ?? 0} />
          <CertificationOverviewCard icon={AlertTriangle} label="Caducados" value={summary?.caducados ?? 0} />
        </div>

        <CertificationFilters
          title="Filtrar documentos"
          description="Combina texto, estado y rangos de fechas para localizar documentación."
          visibleCount={rows.length}
          totalCount={meta?.total ?? rows.length}
          onReset={resetFilters}
          actions={
            <CertificationButton
              variant="secondary"
              icon={loading ? <Loader2 className="animate-spin" size={15} /> : <RefreshCw size={15} />}
              onClick={() => void load()}
              disabled={loading}
            >
              Actualizar
            </CertificationButton>
          }
        >
          <div className="biblioteca-search-shared">
            <Search size={16} aria-hidden="true" />
            <CertificationInputField
              label="Buscar"
              type="search"
              value={filters.search}
              placeholder="Nombre u observaciones"
              onChange={(event: ChangeEvent<HTMLInputElement>) => setFilter("search", event.target.value)}
            />
          </div>
          <CertificationSelectField
            label="Estado"
            value={filters.estado}
            onChange={(event: ChangeEvent<HTMLSelectElement>) =>
              setFilter("estado", event.target.value as BibliotecaFilters["estado"])}
          >
            <option value="">Todos</option>
            <option value="vigente">Vigente</option>
            <option value="proximo_vencer">Próximo a vencer</option>
            <option value="caducado">Caducado</option>
            <option value="sin_vigencia">Sin fecha de vigencia</option>
          </CertificationSelectField>
          <BibliotecaDateRangeFilter
            label="Rango de carga"
            from={filters.carga_desde}
            to={filters.carga_hasta}
            onChange={(from, to) => setFilters((current) => ({
              ...current,
              carga_desde: from,
              carga_hasta: to,
              page: 1,
            }))}
          />
          <BibliotecaDateRangeFilter
            label="Rango de vigencia"
            from={filters.vigencia_desde}
            to={filters.vigencia_hasta}
            onChange={(from, to) => setFilters((current) => ({
              ...current,
              vigencia_desde: from,
              vigencia_hasta: to,
              page: 1,
            }))}
          />
        </CertificationFilters>

        <CertificationDataTable
          title={`Documentación de ${bibliotecaTipoLabels[tipo]}`}
          eyebrow="Biblioteca documental"
          countLabel={`${meta?.total ?? rows.length} documentos`}
          rows={rows}
          columns={columns}
          getRowKey={(document) => document.id}
          loading={loading}
          error={error}
          emptyTitle="No hay documentos para mostrar"
          emptyDescription="Ajusta los filtros o incorpora un nuevo PDF a esta categoría."
          createLabel={canManage ? "Nuevo documento" : undefined}
          onCreate={canManage ? () => {
            setFormError(null);
            setFormDocument(null);
          } : undefined}
          onRetry={() => void load()}
          pagination={{
            currentPage: meta?.current_page ?? 1,
            lastPage: meta?.last_page ?? 1,
            from: meta?.from ?? null,
            to: meta?.to ?? null,
            total: meta?.total ?? rows.length,
            onPageChange: (page) => setFilter("page", page),
          }}
          tableClassName="biblioteca-shared-table"
        />
      </section>

      <BibliotecaDocumentModal
        open={formDocument !== undefined}
        tipo={tipo}
        document={formDocument ?? null}
        submitting={submitting}
        error={formError}
        onClose={() => {
          if (!submitting) setFormDocument(undefined);
        }}
        onUpload={(file, payload) => void submitUpload(file, payload)}
        onUpdate={(payload) => void submitUpdate(payload)}
      />

      <BibliotecaPreviewModal
        standard={standard}
        tipo={tipo}
        document={previewDocument}
        onClose={() => setPreviewDocument(null)}
        onDownload={(document) => void download(document)}
      />

      <BibliotecaDeleteDialog
        document={deleteDocument}
        deleting={submitting}
        error={deleteError}
        onClose={() => {
          if (!submitting) setDeleteDocument(null);
        }}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: BibliotecaDocumento["estado_vigencia"] }) {
  const labels: Record<BibliotecaDocumento["estado_vigencia"], string> = {
    vigente: "Vigente",
    proximo_vencer: "Próximo a vencer",
    caducado: "Caducado",
    sin_vigencia: "Sin fecha de vigencia",
  };

  return <span className={`biblioteca-status biblioteca-status-${status}`}>{labels[status]}</span>;
}

function formatDate(value: string | null): string {
  if (!value) return "Sin fecha";
  return new Intl.DateTimeFormat("es-ES").format(new Date(`${value}T00:00:00`));
}

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatBytes(value: number | null): string {
  if (!value || value <= 0) return "Tamaño no disponible";
  if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}
